<template>
    <base-box
        v-bind="$props"
        v-on="$listeners"
        border-radius="large"
        padding="none"
        :border="hasBorder"
        :border-color="cardBorderColor"
        @click="handleBoxClick"
    >
        <div slot="content" :class="classes">
            <g-media-object :class="{'margin-bottom-normal': isComplete}">
                <template slot="left">
                    <g-icon :name="sideIcon" :class="sideIconColor" :type="sideIconType"/>
                </template>
                <template slot="right">
                    <g-button v-if="proposalAccepted" size="large" shape="round" theme="live" :type="mustDeliver? 'primary' : 'default'" :route="route">
                        {{ mustDeliver ? 'Deliver' : 'View' }}
                    </g-button>
                    <g-button v-else shape="circle" size="large" :icon="rightIcon" theme="live" :route="route" :disabled="isDisabled"/>
                </template>
                <template>
                    <div class="margin-bottom-small">
                        <g-typography tag="h4" :color="titleFontColor" font-weight="semibold" :value="title"/>
                    </div>
                    <div :class="{ 'margin-bottom-tiny' : isComplete }">
                        <g-typography v-if="isError" tag="h5" color="error" font-weight="normal"
                                      :value="errorMessage"/>
                        <g-typography v-else tag="h5" :color="subTitleFontColor" font-weight="normal">
                            <slot :is-complete="isComplete" :is-pending="isPending" :is-started="isStarted" name="subTitle">{{ subTitle }}
                            </slot>
                        </g-typography>
                    </div>
                </template>
            </g-media-object>
            <slot name="completedDetails" v-if="state === 'completed'"/>
        </div>
    </base-box>
</template>

<script>
    import * as _ from "lodash";
    import BaseBox from "../../../library/BaseBox";
    import * as STEP_STATES from "../../../../constants/proposal-step-states";
    import * as STEP_NAMES from '../../../../constants/proposal-steps';
    import {STEP_ERROR_MESSAGES} from "../../../../constants/proposal-step-errors";
    import TasksMixin from "../mixins/TasksMixin";

    const STEP_CARD_ROUTES = {
        [STEP_NAMES.NAME_COMPENSATION]: {name: 'v2.campaign.proposal.compensation'},
        [STEP_NAMES.NAME_PRODUCTS]: {name: 'v2.campaign.proposal.products'},
        [STEP_NAMES.NAME_TASKS]: {name: 'v2.campaign.proposal.tasks'},
        [STEP_NAMES.NAME_NETWORKS]: {name: 'v2.campaign.proposal.networks'},
        [STEP_NAMES.NAME_SHIPPING_INFO]: {name: 'v2.campaign.proposal.shippingInfo'},
        [STEP_NAMES.NAME_PERSONAL_INFO]: {name: 'v2.campaign.proposal.personalInfo'},
        [STEP_NAMES.NAME_CAMPAIGN_ASSETS]: {name: 'v2.campaign.proposal.assets'},
        [STEP_NAMES.NAME_CONTENT_AGREEMENT]: {name: 'v2.campaign.proposal.agreements'},
    };

    const SIDE_ICON_NAMES = {
        [STEP_NAMES.NAME_COMPENSATION]: 'dollar-sign',
        [STEP_NAMES.NAME_PRODUCTS]: 'shopping-cart',
        [STEP_NAMES.NAME_TASKS]: 'list',
        [STEP_NAMES.NAME_NETWORKS]: 'hashtag',
        [STEP_NAMES.NAME_SHIPPING_INFO]: 'home',
        [STEP_NAMES.NAME_PERSONAL_INFO]: 'user',
        [STEP_NAMES.NAME_CAMPAIGN_ASSETS]: 'file-alt',
        [STEP_NAMES.NAME_CONTENT_AGREEMENT]: 'file-signature',
    };

    const SIDE_ICON_TYPES = {
        [STEP_STATES.PENDING]: 'light',
        [STEP_STATES.CURRENT]: 'light',
        [STEP_STATES.COMPLETED]: 'solid',
        [STEP_STATES.ERROR]: 'solid',
    };

    export default {
        name: "GenericStepCard",
        extends: BaseBox,
        components: {
            BaseBox,
        },
        mixins: [
            TasksMixin,
        ],
        props: {
            title: {
                type: String,
                required: true,
            },
            subTitle: {
                type: String,
            },
            isPreview: {
                type: Boolean,
                default: false,
            },
            step: {
                type: Object,
            },
            currentStepName: {
                type: String
            },
            proposalAccepted: {
                type: Boolean,
                default: false,
            },
            proposal: {
                type: Object,
            },
        },
        computed: {
            stepName() {
                return this.step.name;
            },
            route() {
                if (STEP_CARD_ROUTES.hasOwnProperty(this.stepName)) {
                    return STEP_CARD_ROUTES[this.stepName];
                }

                return '/';
            },
            classes() {
                return {
                    'g-step-card': true,
                    [`background-color-${this.state}`]: true,
                };
            },
            titleFontColor() {
                return this.isCurrent ? 'white' : 'normal';
            },
            subTitleFontColor() {
                return this.isCurrent ? 'white' : 'secondary';
            },
            hasBorder() {
                return !(this.isCurrent || this.isPending);
            },
            cardBorderColor() {
                if (this.isError) {
                    return 'red';
                }

                return 'default';
            },
            sideIcon() {
                switch (this.state) {
                    case STEP_STATES.COMPLETED:
                        return 'check-circle';
                    case STEP_STATES.ERROR:
                        return 'exclamation-circle';
                    default:
                        if (SIDE_ICON_NAMES.hasOwnProperty(this.stepName)) {
                            return SIDE_ICON_NAMES[this.stepName];
                        } else {
                            return 'question-circle';
                        }
                }
            },
            sideIconColor() {
                return `side-icon-color-${this.state}`;
            },
            sideIconType() {
                if (SIDE_ICON_TYPES.hasOwnProperty(this.state)) {
                    return SIDE_ICON_TYPES[this.state];
                }

                return 'light';
            },
            rightIcon() {
                return this.state === 'completed' ? 'pencil' : 'arrow-right';
            },
            isDisabled() {
                return this.state === 'pending' && !this.isPreview;
            },
            isError(){
                return this.state === STEP_STATES.ERROR;
            },
            isCurrent(){
              return this.state === STEP_STATES.CURRENT;
            },
            isComplete() {
                return this.state === STEP_STATES.COMPLETED;
            },
            isPending() {
                return this.state === STEP_STATES.PENDING;
            },
            isStarted() {
                return this.step.is_started;
            },
            state() {
                if (!this.step) {
                    return STEP_STATES.PENDING;
                }

                if (this.step.is_started && (!this.step.is_complete || !this.step.is_valid)) {
                    return STEP_STATES.ERROR;
                }

                if (this.stepName === this.currentStepName) {
                    return STEP_STATES.CURRENT;
                }

                if (this.step.is_complete) {
                    return STEP_STATES.COMPLETED;
                }

                return STEP_STATES.PENDING;
            },
            errorMessage() {
                if (!this.step.error) {
                    return '';
                }

                if (STEP_ERROR_MESSAGES[this.step.name] && STEP_ERROR_MESSAGES[this.step.name][this.step.error]) {
                    return STEP_ERROR_MESSAGES[this.step.name][this.step.error];
                } else {
                    return this.step.error.replace(/_/g, ' ');
                }
            },
            isTasksStep() {
                return this.stepName === STEP_NAMES.NAME_TASKS;
            },
            mustDeliver() {
                return this.isTasksStep && _.get(this, 'proposal.tasks', []).some(task => this.hasNetwork(task));
            },
        },
        methods: {
            handleBoxClick() {
                if (!this.isDisabled && this.route) {
                    this.$router.push(this.route);
                }
            }
        },
    }
</script>

<style lang="scss" scoped>
    .g-step-card {
        padding: $g-spacing-6;
    }

    .background-color-pending {
        background-color: $g-color-grey-3;
    }

    .background-color-current {
        background-color: $g-color-grey-7;
    }

    .background-color-error {
        background-color: $g-color-white;
    }

    .background-color-completed {
        background-color: $g-color-white;
    }

    .side-icon-color-pending {
        color: $g-color-grey-5;
    }

    .side-icon-color-current {
        color: $g-color-white;
    }

    .side-icon-color-completed {
        color: $g-color-green-5;
    }

    .side-icon-color-error {
        color: $g-color-red-5;
    }

    .margin-bottom-tiny {
        margin-bottom: $g-spacing-1;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }
</style>
